/*
 * Lemonade Training Oy copy rights all right reserved.
 * Memberships linking several programmings: the read rule every reader uses,
 * the link request fields, refusal details and response shapes.
 */

import type {
  LinkedProgrammingReconcileCounts,
  MembershipType,
} from "./product";
import type { Workout } from "./workout";

export const MAX_LINKED_PROGRAMMINGS = 3 as const;

/**
 * UTF-8 length as TextEncoder and Buffer.byteLength count it: a lone surrogate
 * is 3 bytes (U+FFFD). Hand-rolled because jsdom test environments lack TextEncoder.
 */
function utf8EncodedLength(value: string): number {
  let bytes = 0;
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code < 0x80) {
      bytes += 1;
    } else if (code < 0x800) {
      bytes += 2;
    } else if (code >= 0xd800 && code <= 0xdbff && i + 1 < value.length) {
      const next = value.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i += 1;
      } else {
        bytes += 3;
      }
    } else {
      bytes += 3;
    }
  }
  return bytes;
}

/** Firestore's document id rule; the length limit is in UTF-8 bytes. */
export function isFirestoreDocId(value: unknown): value is string {
  if (typeof value !== "string") return false;
  if (value === "" || value === "." || value === "..") return false;
  if (value.includes("/")) return false;
  if (/^__.*__$/.test(value)) return false;
  return utf8EncodedLength(value) <= 1500;
}

/** Raw stored fields; unknown because planted or legacy documents are read too. */
export type LinkedProgrammingLinkFields = {
  linkedProgrammingUID?: unknown;
  linkedProgrammingUIDs?: unknown;
};

export type LinkedProgrammingLinkSource = "array" | "scalar" | "none";

export type LinkedProgrammingLinkState = {
  /** 0..MAX_LINKED_PROGRAMMINGS unique doc ids, element 0 = Main. */
  uids: string[];
  source: LinkedProgrammingLinkSource;
  /** The array was present but disagreed with the scalar, so the scalar won. */
  mismatch: boolean;
  /** An agreeing array held more than MAX_LINKED_PROGRAMMINGS usable ids. */
  truncated: boolean;
  /** Agreeing-array elements removed as duplicates or invalid ids. */
  dropped: number;
};

/**
 * The array is used only when it agrees with the valid scalar (array[0] ===
 * scalar, or both empty); otherwise the scalar alone. A present non-array
 * disagrees.
 */
export function linkedProgrammingLinkState(
  doc: LinkedProgrammingLinkFields | null | undefined,
): LinkedProgrammingLinkState {
  const fields = doc || {};
  const scalar = isFirestoreDocId(fields.linkedProgrammingUID)
    ? fields.linkedProgrammingUID
    : null;
  const raw = fields.linkedProgrammingUIDs;
  const scalarOnly = (mismatch: boolean): LinkedProgrammingLinkState => ({
    uids: scalar ? [scalar] : [],
    source: scalar ? "scalar" : "none",
    mismatch,
    truncated: false,
    dropped: 0,
  });

  if (raw === undefined || raw === null) return scalarOnly(false);
  if (!Array.isArray(raw)) return scalarOnly(true);
  const agrees =
    raw.length > 0 ? scalar !== null && raw[0] === scalar : scalar === null;
  if (!agrees) return scalarOnly(true);

  const seen = new Set<string>();
  let dropped = 0;
  for (const value of raw) {
    if (isFirestoreDocId(value) && !seen.has(value)) {
      seen.add(value);
    } else {
      dropped += 1;
    }
  }
  const usable = Array.from(seen);
  return {
    uids: usable.slice(0, MAX_LINKED_PROGRAMMINGS),
    source: "array",
    mismatch: false,
    truncated: usable.length > MAX_LINKED_PROGRAMMINGS,
    dropped,
  };
}

export function linkedProgrammingUIDsOf(
  doc: LinkedProgrammingLinkFields | null | undefined,
): string[] {
  return linkedProgrammingLinkState(doc).uids;
}

/** createProduct / updateProduct link fields (Membership only). */
export type LinkedProgrammingLinkUpdate = {
  linkedProgrammingUIDs?: string[];
  /**
   * @deprecated Send linkedProgrammingUIDs. When both are sent it must equal
   * linkedProgrammingUIDs[0] (null when empty).
   */
  linkedProgrammingUID?: string | null;
  /** The list the dialog loaded; the save is refused when the stored list changed since. */
  expectedLinkedProgrammingUIDs?: string[];
};

export const LINKED_PROGRAMMING_REJECTION_REASONS = [
  "notArray",
  "tooMany",
  "duplicate",
  "invalidId",
  "conflict",
  "scalarOnMultiLink",
  "withStripeFields",
  "notMembership",
  "notLinkable",
  "pt",
  "archived",
  "foreign",
  "noBoxID",
] as const;
export type LinkedProgrammingRejectionReason =
  (typeof LINKED_PROGRAMMING_REJECTION_REASONS)[number];

/** HttpsError `details` of a link refusal; never a name or a member uid. */
export type LinkedProgrammingRejection = {
  reason: LinkedProgrammingRejectionReason;
  /** Element index, or null for a request-level reason. */
  index: number | null;
  programmingUID: string | null;
};

export function isLinkedProgrammingRejection(
  details: unknown,
): details is LinkedProgrammingRejection {
  if (typeof details !== "object" || details === null) return false;
  const { reason, index, programmingUID } = details as Record<string, unknown>;
  return (
    typeof reason === "string" &&
    (LINKED_PROGRAMMING_REJECTION_REASONS as readonly string[]).includes(
      reason,
    ) &&
    (index === null ||
      (typeof index === "number" && Number.isInteger(index) && index >= 0)) &&
    (programmingUID === null || typeof programmingUID === "string")
  );
}

export type LinkedProgrammingChange = "added" | "removed" | "kept";

export type LinkedProgrammingElementCounts = Pick<
  LinkedProgrammingReconcileCounts,
  | "granted"
  | "repointed"
  | "revoked"
  | "unchanged"
  | "keptDirectPurchase"
  | "skippedNotGranting"
  | "skippedNoUser"
  | "skippedMissingProgramming"
  | "skippedIneligibleProgramming"
  | "superseded"
> & { change: LinkedProgrammingChange };

/** A linked programme as the public landing page shows it; an explicit allowlist. */
export type LandingLinkedProgramme = {
  uid: string;
  name: string;
  coverArtURL: string | null;
  description: string | null;
};

export type LandingMembershipLinks = {
  /** linkedProgrammings[0]; absent when none. Kept for older bundles. */
  linkedProgramming?: LandingLinkedProgramme;
  /** Eligible elements in link order; absent from older function instances. */
  linkedProgrammings?: LandingLinkedProgramme[];
};

export type LinkedProgrammingRef = Pick<LandingLinkedProgramme, "uid" | "name">;

/** getVisitCardTodaysWorkout response. */
export type VisitCardTodaysWorkoutResponse = {
  workouts: Workout[];
  /** The first contributing programme. */
  programmingName: string | null;
  boxName: string | null;
  /** Absent on the empty answer. */
  boxID?: string;
  /** Programmes that contributed a workout, in link order. */
  programmings?: LinkedProgrammingRef[];
};

/** How a membership grants a programme userTodaysWorkout lists. */
export type TodaysWorkoutMembershipSource = {
  /** The Users/{uid}/BoxMemberships row id, which is the membership productUID. */
  productUID: string;
  membershipType: MembershipType;
  /** The membership product's name; null when unreadable. */
  name: string | null;
  boxID: string;
  boxName: string | null;
  /** Position in the membership's link order, 0 = Main; null once it is no longer listed. */
  linkIndex: number | null;
};

/** One entry of the userTodaysWorkout response. */
export type TodaysWorkoutProgramme = {
  id: string;
  name: string;
  data: Array<Workout & { UID: string }>;
  /** Only on a programme a membership granted; absent from older functions. */
  membership?: TodaysWorkoutMembershipSource;
};

/** Own programmes first, then each membership's in link order. */
export type UserTodaysWorkoutResponse = TodaysWorkoutProgramme[];
